import type { VmExecutionMode } from "@t3tools/contracts";
import {
  BoxIcon,
  CloudIcon,
  FolderGit2Icon,
  FolderGitIcon,
  FolderIcon,
  HistoryIcon,
} from "lucide-react";
import { memo, useMemo } from "react";

import type { VmComposerExecutionMode } from "../vmSetupWorkspace";
import {
  resolveBranchToolbarTarget,
  resolveBranchToolbarTargetChange,
  resolveCurrentWorkspaceLabel,
  resolveEnvModeLabel,
  resolveLockedBranchToolbarTargetLabel,
  resolveBranchToolbarTargetLabel,
  shouldShowVmConfigureButton,
  type BranchToolbarTarget,
  type EnvMode,
} from "./BranchToolbar.logic";
import { Button } from "./ui/button";
import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export const PREVIOUS_WORKTREE_SELECT_VALUE = "previous-worktree";
export const FABRICA_CLOUD_SETUP_SELECT_VALUE = "fabrica-cloud-setup";

interface BranchToolbarEnvModeSelectorProps {
  envLocked: boolean;
  effectiveEnvMode: EnvMode;
  activeWorktreePath: string | null;
  onEnvModeChange: (mode: EnvMode) => void;
  executionMode: VmComposerExecutionMode;
  onExecutionModeChange: (mode: VmExecutionMode) => void;
  vmConfigured?: boolean | undefined;
  onConfigureVm?: (() => void) | undefined;
  onConfigureCloud?: (() => void) | undefined;
  showWorktreeOption?: boolean;
  previousWorktreeLabel?: string | null;
  onUsePreviousWorktree?: () => void;
}

export const BranchToolbarEnvModeSelector = memo(function BranchToolbarEnvModeSelector({
  envLocked,
  effectiveEnvMode,
  activeWorktreePath,
  onEnvModeChange,
  executionMode,
  onExecutionModeChange,
  vmConfigured,
  onConfigureVm,
  onConfigureCloud,
  showWorktreeOption = true,
  previousWorktreeLabel,
  onUsePreviousWorktree,
}: BranchToolbarEnvModeSelectorProps) {
  const showPreviousWorktree = Boolean(
    showWorktreeOption && previousWorktreeLabel && onUsePreviousWorktree,
  );
  const resolvedTarget = resolveBranchToolbarTarget({ effectiveEnvMode, executionMode });
  const target: BranchToolbarTarget =
    resolvedTarget === "worktree" && !showWorktreeOption ? "local" : resolvedTarget;
  const targetLabel = resolveBranchToolbarTargetLabel({
    target,
    activeWorktreePath,
    vmConfigured,
  });
  const envModeItems = useMemo(
    () => [
      { value: "local", label: resolveCurrentWorkspaceLabel(activeWorktreePath) },
      ...(showWorktreeOption
        ? [{ value: "worktree", label: resolveEnvModeLabel("worktree") }]
        : []),
      { value: "vm", label: "VM" },
      ...(onConfigureCloud
        ? [{ value: FABRICA_CLOUD_SETUP_SELECT_VALUE, label: "Fabrica Cloud" }]
        : []),
      ...(showPreviousWorktree && previousWorktreeLabel
        ? [{ value: PREVIOUS_WORKTREE_SELECT_VALUE, label: previousWorktreeLabel }]
        : []),
    ],
    [
      activeWorktreePath,
      onConfigureCloud,
      previousWorktreeLabel,
      showPreviousWorktree,
      showWorktreeOption,
    ],
  );

  const handleTargetChange = (nextTarget: BranchToolbarTarget) => {
    const next = resolveBranchToolbarTargetChange(nextTarget);
    onEnvModeChange(next.envMode);
    onExecutionModeChange(next.executionMode);
  };

  const showVmConfigureButton =
    shouldShowVmConfigureButton({ target }) && onConfigureVm !== undefined;
  const configureButton = showVmConfigureButton ? (
    <Button
      variant="ghost-muted"
      size="xs"
      className="text-muted-foreground/70 hover:text-foreground/80"
      aria-label="Configure VM environment"
      onClick={onConfigureVm}
      data-composer-context-control
    >
      Configure
    </Button>
  ) : null;
  if (envLocked) {
    const Icon =
      target === "vm"
        ? BoxIcon
        : target === "worktree"
          ? FolderGit2Icon
          : activeWorktreePath
            ? FolderGitIcon
            : FolderIcon;
    return (
      <>
        <span
          className="inline-flex h-7 shrink-0 items-center gap-1 border border-transparent px-[calc(--spacing(3)-1px)] text-sm font-medium text-muted-foreground/70 sm:h-6 sm:text-xs"
          data-composer-context-control
        >
          <Icon className="size-3" />
          {resolveLockedBranchToolbarTargetLabel({
            target,
            activeWorktreePath,
            vmConfigured,
          })}
        </span>
        {configureButton}
      </>
    );
  }

  return (
    <>
      <Select
        modal={false}
        value={target}
        onValueChange={(value: string | null) => {
          if (value === PREVIOUS_WORKTREE_SELECT_VALUE) {
            onExecutionModeChange("host");
            onUsePreviousWorktree?.();
            return;
          }
          if (value === FABRICA_CLOUD_SETUP_SELECT_VALUE) {
            onConfigureCloud?.();
            return;
          }
          if (value === "local" || value === "worktree" || value === "vm") {
            handleTargetChange(value);
          }
        }}
        items={envModeItems}
      >
        <SelectTrigger
          variant="ghost"
          size="xs"
          className="min-w-0 shrink font-medium"
          aria-label="Run target"
          data-composer-context-control
        >
          {target === "vm" ? (
            <BoxIcon className="size-3" />
          ) : target === "worktree" ? (
            <FolderGit2Icon className="size-3" />
          ) : activeWorktreePath ? (
            <FolderGitIcon className="size-3" />
          ) : (
            <FolderIcon className="size-3" />
          )}
          <span
            data-composer-label
            className="min-w-0 max-w-[240px] group-data-[compact]/composer-context:max-w-0"
          >
            <span
              data-composer-label-motion
              className="block w-full min-w-0 max-w-[240px] origin-left truncate transition-[opacity,transform] duration-180 ease-[cubic-bezier(0.32,0.72,0,1)] group-data-[compact]/composer-context:[transform:translateX(-0.25rem)_scaleX(0.95)] group-data-[compact]/composer-context:opacity-0 motion-reduce:transform-none motion-reduce:transition-opacity"
            >
              <SelectValue>{targetLabel}</SelectValue>
            </span>
          </span>
        </SelectTrigger>
        <SelectPopup>
          <SelectGroup>
            <SelectGroupLabel>Run target</SelectGroupLabel>
            <SelectItem value="local">
              <span className="inline-flex items-center gap-1.5">
                {activeWorktreePath ? (
                  <FolderGitIcon className="size-3" />
                ) : (
                  <FolderIcon className="size-3" />
                )}
                {resolveCurrentWorkspaceLabel(activeWorktreePath)}
              </span>
            </SelectItem>
            {showWorktreeOption ? (
              <SelectItem value="worktree">
                <span className="inline-flex items-center gap-1.5">
                  <FolderGit2Icon className="size-3" />
                  {resolveEnvModeLabel("worktree")}
                </span>
              </SelectItem>
            ) : null}
            <SelectItem value="vm">
              <span className="inline-flex items-center gap-1.5">
                <BoxIcon className="size-3" />
                VM
              </span>
            </SelectItem>
            {showPreviousWorktree && previousWorktreeLabel ? (
              <SelectItem value={PREVIOUS_WORKTREE_SELECT_VALUE}>
                <span className="inline-flex items-center gap-1.5">
                  <HistoryIcon className="size-3" />
                  {previousWorktreeLabel}
                </span>
              </SelectItem>
            ) : null}
          </SelectGroup>
          {onConfigureCloud ? (
            <>
              <SelectSeparator />
              <SelectGroup>
                <SelectGroupLabel>Cloud</SelectGroupLabel>
                <SelectItem value={FABRICA_CLOUD_SETUP_SELECT_VALUE}>
                  <span className="flex w-full min-w-0 items-center gap-1.5">
                    <CloudIcon className="size-3" />
                    <span className="min-w-0 flex-1 truncate">Fabrica Cloud</span>
                    <span className="text-xs text-muted-foreground">Set up</span>
                  </span>
                </SelectItem>
              </SelectGroup>
            </>
          ) : null}
        </SelectPopup>
      </Select>
      {configureButton}
    </>
  );
});
